<template>
  <div v-if="customer" class="flex flex-col h-full overflow-hidden">
    <!-- Header -->
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      :title="modifiedCustomer?.trader?.name ? modifiedCustomer.trader.name : '-'"
      link-name="customers"
      :is-modified="isModified"
      :is-edit-active="customer.isFromManager && !customer.deleted"
      :mode="mode"
      @edit-mode="onEditMode"
      @cancel="onCancelEdit"
      @save="onSaveChanges"
      @horizontal-layout="(value: boolean) => (isHorizontalLayout = value)" />

    <div v-if="isHorizontalLayout">
      <nav
        role="section-nav"
        class="flex justify-between px-6 text-sm font-medium text-center text-gray-500 sm:px-0 min-h-9">
        <ul class="flex flex-wrap items-center gap-4 pl-4 -mb-px">
          <li
            v-for="tab in section1Tabs"
            :key="tab.name"
            @click="selectedTab = tab.name"
            class="py-1.5 cursor-pointer"
            :class="[
              selectedTab === tab.name ? 'border-b-2 border-theme-500' : '',
              tab.validator && $v[tab.validator].required.$invalid
                ? 'text-red-500 !border-red-500'
                : '',
            ]">
            <p>
              {{ tab.label }}
            </p>
          </li>
        </ul>
      </nav>
    </div>
    <div>
      <!-- Horizontal Layout -->
      <div v-if="isHorizontalLayout">
        <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
          <component
            :is="currentComponent"
            v-model="modifiedCustomer"
            v-model:assignedRanges="assignedRanges"
            v-model:isRangeModified="isRangesModified"
            :customer="customer"
            :item="customer"
            enable-ranges
            :mode="mode"
            :validations="$v"
            :isHorizontalLayout="isHorizontalLayout"
            :key="customer?.id"
            :reset-state="resetState" />
        </PerfectScrollbar>
      </div>

      <!-- Vertical Layout -->
      <div v-else>
        <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
          <BaseAccordion
            v-for="tab in section1Tabs"
            :key="tab.name"
            :name="tab.label"
            :error="tab.validator && $v[tab.validator].required.$invalid">
            <CustomerDetailsTabProperties
              v-if="tab.name === 'properties'"
              v-model="modifiedCustomer"
              :mode="mode"
              :validations="$v"
              :isHorizontalLayout="isHorizontalLayout" />

            <DetailsTabTrader
              v-else-if="tab.name === 'trader'"
              v-model="modifiedCustomer"
              :item="customer"
              :mode="mode"
              :validations="$v"
              :isHorizontalLayout="isHorizontalLayout" />

            <CustomerDetailsTabTraderSite
              v-else-if="tab.name === 'traderSite'"
              v-model="modifiedCustomer"
              v-model:assignedRanges="assignedRanges"
              v-model:isRangeModified="isRangesModified"
              :mode="mode"
              enable-ranges
              :isHorizontalLayout="isHorizontalLayout"
              :reset-state="resetState" />

            <CustomerDetailsTabPricingPolicy
              v-else-if="tab.name === 'pricingPolicy'"
              v-model="modifiedCustomer"
              :mode="mode"
              :isHorizontalLayout="isHorizontalLayout">
            </CustomerDetailsTabPricingPolicy>

            <CustomerDetailsTabFinancialInfo
              v-else-if="tab.name === 'financialInfo'"
              :customer="customer"
              :mode="mode">
            </CustomerDetailsTabFinancialInfo>
          </BaseAccordion>
        </PerfectScrollbar>
      </div>
    </div>

    <BaseDrawerConfirm
      :is-active="confirmModalActive"
      @cancel="confirmModalActive = false"
      @confirm="onDiscardChanges" />
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import BaseAccordion from '@/components/common/BaseAccordion.vue';
import type { PerfectScrollbarExpose } from 'vue3-perfect-scrollbar';
import CustomerDetailsTabProperties from '@/components/traders/CustomerDetailsTabProperties.vue';
import DetailsTabTrader from '@/components/traders/DetailsTabTrader.vue';
import CustomerDetailsTabTraderSite from '@/components/traders/CustomerDetailsTabTraderSite.vue';
import CustomerDetailsTabPricingPolicy from '@/components/traders/CustomerDetailsTabPricingPolicy.vue';
import type { Customer, Trader } from '@/types/Traders';
import { addOrEditCustomer, getCustomerById } from '@/services/api/traders';
import { useI18n } from 'vue-i18n';
import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';
import useVuelidate from '@vuelidate/core';
import { useToastStore } from '@/stores/toast';
import { helpers, integer, maxValue, minValue, required } from '@vuelidate/validators';
import CustomerDetailsTabFinancialInfo from '@/components/traders/CustomerDetailsTabFinancialInfo.vue';
import { deepCopy } from '@/utils/data';
import { v4 as uuidv4 } from 'uuid';
import type { RangeCustomerTradersite } from '@/types/Ranges';
import { addEditRangeCustomerTradersites } from '@/services/api/ranges';

const { getUsersLookup } = useLookupsStore();
const { users } = storeToRefs(useLookupsStore());
const { t: $t } = useI18n();
const customer = ref<Customer>();
const modifiedCustomer = ref<Customer>();
const isHorizontalLayout = ref(true);
const scrollbarId = ref<PerfectScrollbarExpose>();
const mode = ref<'view' | 'edit'>('view');
const route = useRoute();
const router = useRouter();
const toastStore = useToastStore();
const isModified = ref<boolean>(false);
const confirmModalActive = ref(false);
const selectedTab = ref('properties');
const section1Tabs = ref([
  {
    name: 'properties',
    label: $t('customer.properties'),
    component: markRaw(CustomerDetailsTabProperties),
    validator: 'defaultPaymentType',
  },
  {
    name: 'trader',
    label: $t('customer.trader'),
    component: markRaw(DetailsTabTrader),
    validator: 'traderTaxNo',
  },
  {
    name: 'traderSite',
    label: $t('customer.defaultTraderSite'),
    component: markRaw(CustomerDetailsTabTraderSite),
  },
  {
    name: 'pricingPolicy',
    label: $t('pricing.pricingPolicy'),
    component: markRaw(CustomerDetailsTabPricingPolicy),
  },
  {
    name: 'financialInfo',
    label: $t('customer.financialInfo'),
    component: markRaw(CustomerDetailsTabFinancialInfo),
  },
]);
const currentComponent = computed(() => {
  const selected = section1Tabs.value.find((tab) => tab.name === selectedTab.value);
  return selected ? selected.component : null;
});
const isRangesModified = ref(false);
watch(
  () => modifiedCustomer.value,
  (newData) => {
    if (newData?.traderSites?.length && customer.value)
      customer.value.traderSites = newData.traderSites;
    isModified.value =
      JSON.stringify(newData) !== JSON.stringify(customer.value) || isRangesModified.value;
    validationModel.value.defaultPaymentType = newData?.defaultPaymentTypeId || '';
    validationModel.value.traderName = newData?.trader?.name || '';
    validationModel.value.traderCode = newData?.trader?.code || '';
    validationModel.value.traderTaxNo = newData?.trader?.taxNumber || '';
    validationModel.value.paymentDays = newData?.paymentDays || '';
    validationModel.value.discountPercentage = newData?.discountpercentage || '';
  },
  { deep: true }
);

const assignedRanges = ref<RangeCustomerTradersite[]>([]);

onMounted(async () => {
  await loadCustomer();
});

async function loadCustomer() {
  const res = await getCustomerById(route.params.id as string);
  if (res) {
    customer.value = res as Customer;
    await populateUsersFromLookups();
    mode.value = 'view';
  } else {
    const traderId = uuidv4();
    customer.value = {
      id: route.params.id as string,
      isFromManager: true,
      isActive: true,
      code: '',
      traderId: traderId,
      trader: { id: traderId, name: '', code: '' } as Trader,
      traderSites: [],
    };
    mode.value = 'edit';
  }
  modifiedCustomer.value = deepCopy<Customer>(customer.value);
}

async function populateUsersFromLookups() {
  await getUsersLookup();
  if (customer.value?.createUserId)
    customer.value.createUser = users.value.find((x) => x.id == customer.value?.createUserId);
  if (customer.value?.updateUserId)
    customer.value.updateUser = users.value.find((x) => x.id == customer.value?.updateUserId);
}

const isBackMode = ref(false);
const resetState = ref(false);
function onCancelEdit(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') {
      isBackMode.value = true;
    }
    return;
  } else if (!customer.value?.createdDate) {
    router.push('/customers');
  }
  mode.value = 'view';
  resetState.value = true;
}

function onDiscardChanges() {
  mode.value = 'view';
  confirmModalActive.value = false;
  if (customer.value) modifiedCustomer.value = deepCopy<Customer>(customer.value);
  resetState.value = true;
  if (!customer.value?.createdDate || isBackMode.value) {
    router.push('/customers');
  }
}

const validationModel = ref({
  defaultPaymentType: modifiedCustomer.value?.defaultPaymentTypeId || '',
  traderName: modifiedCustomer.value?.trader?.name || '',
  traderCode: modifiedCustomer.value?.trader?.code || '',
  traderTaxNo: modifiedCustomer.value?.trader?.taxNumber || '',
  paymentDays: modifiedCustomer.value?.paymentDays || '',
  discountPercentage: modifiedCustomer.value?.discountpercentage || '',
});

const rules = computed(() => ({
  defaultPaymentType: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  traderName: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  traderCode: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  traderTaxNo: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  paymentDays: {
    integer: helpers.withMessage(`${$t('messages.errors.integer')}`, integer),
  },
  discountPercentage: {
    minValue: helpers.withMessage(`${$t('messages.errors.minValue')} 0`, minValue(0)),
    maxValue: helpers.withMessage(`${$t('messages.errors.maxValue')} 100`, maxValue(100)),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

async function onSaveChanges() {
  $v.value.$touch();
  if ($v.value.$invalid) {
    return;
  }
  mode.value = 'view';
  isModified.value = false;

  if (assignedRanges.value.length) {
    const res = await addEditRangeCustomerTradersites(assignedRanges.value);
    if (res) {
      toastStore.success({ text: `${$t('common.toast.success')}` });
      resetState.value = true;
    } else {
      toastStore.error({ text: res });
    }
    isRangesModified.value = false;
  }
  if (modifiedCustomer.value) {
    const res = await addOrEditCustomer(modifiedCustomer.value);
    if (res) {
      toastStore.success({ text: `${$t('common.toast.success')}` });
      await loadCustomer();
      resetState.value = true;
    } else {
      toastStore.error({ text: res });
    }
  }
}

function onEditMode() {
  mode.value = 'edit';
  resetState.value = false;
}
</script>
